import { useMe } from '@/api/hooks/useMe'
import { IndividualType } from '@/hooks/useIndividuals'

import { useSubscriptionRealtime } from './useSubscriptionRealtime'

const KNOWN_STATUSES = ['trial', 'active', 'expired'] as const

export type SubscriptionState = {
  /**
   * Starting an errand. Same answer as `canUseChat` today, because an errand is a
   * conversation — creating one creates its thread — but a separate flag so the two can
   * part company without every caller having to know they were ever the same.
   */
  canCreateErrands: boolean
  /**
   * Composing in the chat. False on a trial as well as when read-only — the trial is documents
   * and the Hitta stöd generation they produce, and chat is not part of it. Reading an existing
   * conversation stays available either way.
   */
  canUseChat: boolean
  /** False once a trial has used its allowance. Always true when unlimited. */
  canUploadDocuments: boolean
  /** null means unlimited. */
  documentLimit: number | null
  documentsRemaining: number | null
  documentsUsed: number
  /**
   * Whether a payment provider is behind this, and so whether the customer portal has
   * anything to manage. Not the same as "active": after the billing backfill every
   * pre-existing individual is active with no provider subscription at all, and offering
   * those a Manage button sends the user to an endpoint that 404s.
   */
  hasProviderBilling: boolean
  /** When access runs out. Undefined means open-ended — only ever a manual grant. */
  currentPeriodEnd?: string
  /** Whether the signed-in user is the subscription holder. */
  isOwner: boolean
  /** Every mutation is blocked. Reads and file downloads still work. */
  isReadOnly: boolean
  isTrial: boolean
  status: 'trial' | 'active' | 'expired'
}

/**
 * Single source of subscription UI state: merges live RTDB status over the individual
 * response. Called once by SubscriptionProvider so the whole app shares one RTDB
 * subscription — consumers read it via `useSubscription()`.
 *
 * This is UX only. The backend is the enforcement boundary and rejects with 402
 * regardless of what the UI shows.
 *
 * `individual` is optional because the provider mounts before one is selected; without
 * one this returns permissive inert state and opens no subscription.
 */
export const useSubscriptionState = (individual?: IndividualType): SubscriptionState => {
  const live = useSubscriptionRealtime(individual?.id)
  const { data: me } = useMe()

  const subscription = individual?.subscription

  // Live status wins, then the response. Absent on individuals created before the
  // field existed, which the backend grandfathers as 'active' — mirror that here
  // rather than defaulting to something restrictive the server wouldn't enforce.
  //
  // The live value is only trusted when it is one this app understands. The realtime node
  // is written by a Payload hook rather than by this app's own API, so it is the one place
  // a backend-internal status could leak in — and an unrecognised one would not throw, it
  // would quietly fail every comparison below: not trial, not expired, no document limit.
  const liveStatus = KNOWN_STATUSES.includes(live?.status as SubscriptionState['status'])
    ? (live?.status as SubscriptionState['status'])
    : undefined

  const status = liveStatus ?? subscription?.status ?? 'active'

  // The counts only come from the response. A live status change invalidates the
  // individual query, so they arrive on the next render.
  const documentsUsed = subscription?.documentsUsed ?? 0
  const documentLimit = status === 'trial' ? (subscription?.documentLimit ?? null) : null
  const documentsRemaining =
    documentLimit === null ? null : Math.max(0, documentLimit - documentsUsed)

  return {
    canCreateErrands: status === 'active',
    canUploadDocuments:
      status !== 'expired' && (documentsRemaining === null || documentsRemaining > 0),
    // Derived from `status` rather than read off the response, so a live RTDB status change locks
    // the chat on the same render as the rest of the UI instead of waiting for a refetch.
    canUseChat: status === 'active',
    documentLimit,
    documentsRemaining,
    documentsUsed,
    currentPeriodEnd: subscription?.currentPeriodEnd,
    hasProviderBilling: subscription?.hasProviderBilling ?? false,
    isOwner: !!subscription?.ownerId && subscription.ownerId === me?.id,
    isReadOnly: status === 'expired',
    isTrial: status === 'trial',
    status,
  }
}
